import * as React                    from 'react';

import { Button                    } from 'src/components/common/button';
import { Dialog                    } from 'src/components/common/dialog';
import { IconButton                } from 'src/components/common/icon-button';
import { InputTextbox              } from 'src/components/common/input/input-textbox';
import { Labeled                   } from 'src/components/common/labeled';
import { TabSwitch,
         TabItem                   } from 'src/components/common/tab-switch';

import { Container,
         Caption,
         ResultContainer,
         Result,
         ResultLabel,
         ResultValue,
         Close,
         CloseOutlineVectorBig,
         DefectCalcDialogVariants,
         defectcalcdialog_variants } from 'src/components/features/inspection/edit/defect-calc-dialog/ui';

import Util                          from 'src/services/util';



type RSS = Record<string, string>;

type Props = {
  variant: DefectCalcDialogVariants;
  opened: boolean;
  isAreaAcceptable: boolean;
  onAccept: (value: string, tab: 'weight' | 'weightpipe' | 'area') => void;
  onClose: () => void;
}
export const DefectCalcDialog: React.FC<Props> = ({
  variant,
  opened,
  isAreaAcceptable,
  onAccept,
  onClose,
}) => {
  const lookAndFeel = defectcalcdialog_variants.get(variant)!;

  const tabs: TabItem[] = [
    {
      id: 'weight',
      label: 'Вес',
    },
    {
      id: 'weightpipe',
      label: 'Вес трубы',
    },
    {
      id: 'area',
      label: 'Площадь',
    },
    {
      id: 'length',
      label: 'Длина',
    }
  ];

  const captions: RSS = { weight: 'Фактический вес', weightpipe: 'Фактический вес', area: 'Фактическая площадь', length: 'Фактическая длина' };
  const rlabels: RSS = { weight: 'Вес:', weightpipe: 'Вес:', area: 'Площадь:', length: 'Длина:' };
  const runits: RSS = { weight: 'кг', weightpipe: 'кг', area: 'м2', length: 'м' };

  const [activeTab, setActiveTab] = React.useState<TabItem>(tabs[0]);
  const [weight, setWeight] = React.useState<string>('');
  const [weightPipe, setWeightPipe] = React.useState<string>('');
  const [thickness, setThickness] = React.useState<string>('');
  const [widthmm, setWidthMM] = React.useState<string>('');
  const [widthm, setWidthM] = React.useState<string>('');
  const [length, setLength] = React.useState<string>('');
  const [countMarriage, setCountMarriage] = React.useState<string>('');
  const [countPipes, setCountPipes] = React.useState<string>('');
  const [count, setCount] = React.useState<string>('1');
  
  let result = 0;

  switch (activeTab.id)
  {
    case 'weight':
      if (thickness !== '' && widthmm !== '' && length !== '' && count !== '')
      {
        result = (parseInt(count, 10) * parseFloat(thickness) * parseFloat(widthmm) * parseFloat(length) * 7.85) / 1000;
      }
      break;
    case 'weightpipe':
      if (weightPipe !== '' && countMarriage !== '' && countPipes !== '')
      {
        result = (parseFloat(weightPipe) / parseInt(countPipes, 10)) * parseInt(countMarriage, 10);
      }
      break;
    case 'length':
      if (weight !== '' && thickness !== '' && widthmm !== '')
      {
        result = (parseFloat(weight) / (parseFloat(thickness) * parseFloat(widthmm) * 7.85)) * 1000000;
      }
      break;
    case 'area':
      if (widthm !== '' && length !== '')
      {
        result = parseFloat(widthm) * parseFloat(length);
      }
      break;
  }

  React.useEffect(() => {
    if (opened)
    {
      setActiveTab(tabs[0]);
      setWeight('');
      setWeightPipe('');
      setThickness('');
      setWidthMM('');
      setWidthM('');
      setLength('');
      setCountPipes('');
      setCountMarriage('');
      setCount('1');
    }
  }, [opened]);

  const onTapAcceptHandler = () => {
    onAccept(
      ['weight', 'weightpipe'].includes(activeTab.id) ? `${Math.round(result)}` : `${Math.round(result * 100) / 100}`,
      activeTab.id as ('weight' | 'weightpipe' | 'area'));
  }

  return (
    <Dialog variant = { lookAndFeel.dialogVariant } isOpened = { opened }>
      <Container lookAndFeel = { lookAndFeel }>
        <Close lookAndFeel = { lookAndFeel }>
          <IconButton
            variant = { lookAndFeel.closeButtonVariant }
            isTapAllowed
            onTap = { onClose }
          >
            <CloseOutlineVectorBig />
          </IconButton>
        </Close>
        <React.Fragment>
          <Caption lookAndFeel = { lookAndFeel }>
            { captions[activeTab.id] }
          </Caption>
          <TabSwitch
            variant = { lookAndFeel.tabSwitchVariant }
            value = { activeTab }
            items = { tabs }
            onChange = { (tab) => setActiveTab(tab) }
          />
          {['length'].includes(activeTab.id) && (
            <Labeled label = 'Вес, т' variant = { lookAndFeel.labeledVariant }>
              <InputTextbox
                variant = { lookAndFeel.textboxVariant }
                value = { weight }
                valueTransformer = { (value: string) => Util.selectDigitsAndDotFromStringN_M(value, 6, 3) }
                onChange = { (value) => setWeight(value) }
              />
            </Labeled>
          )}
          {['weightpipe'].includes(activeTab.id) && (
            <Labeled label = 'Вес нетто пачки, кг' variant = { lookAndFeel.labeledVariant }>
              <InputTextbox
                variant = { lookAndFeel.textboxVariant }
                value = { weightPipe }
                valueTransformer = { (value: string) => Util.selectDigitsAndDotFromStringN_M(value, 6, 3) }
                onChange = { (value) => setWeightPipe(value) }
              />
            </Labeled>
          )}
          {['weight', 'length'].includes(activeTab.id) && (
            <Labeled label = 'Толщина, мм' variant = { lookAndFeel.labeledVariant }>
              <InputTextbox
                variant = { lookAndFeel.textboxVariant }
                value = { thickness }
                valueTransformer = { (value: string) => Util.selectDigitsAndDotFromStringN_M(value, 4, 3) }
                onChange = { (value) => setThickness(value) }
              />
            </Labeled>
          )}
          {['weight', 'length'].includes(activeTab.id) && (
            <Labeled label = 'Ширина, мм' variant = { lookAndFeel.labeledVariant }>
              <InputTextbox
                variant = { lookAndFeel.textboxVariant }
                value = { widthmm }
                valueTransformer = { (value: string) => Util.selectDigitsAndDotFromStringN_M(value, 4, 3) }
                onChange = { (value) => setWidthMM(value) }
              />
            </Labeled>
          )}
          {['area'].includes(activeTab.id) && (
            <Labeled label = 'Ширина, м' variant = { lookAndFeel.labeledVariant }>
              <InputTextbox
                variant = { lookAndFeel.textboxVariant }
                value = { widthm }
                valueTransformer = { (value: string) => Util.selectDigitsAndDotFromStringN_M(value, 4, 3) }
                onChange = { (value) => setWidthM(value) }
              />
            </Labeled>
          )}
          {['weight', 'area'].includes(activeTab.id) && (
            <Labeled label = 'Длина, м' variant = { lookAndFeel.labeledVariant }>
              <InputTextbox
                variant = { lookAndFeel.textboxVariant }
                value = { length }
                valueTransformer = { (value: string) => Util.selectDigitsAndDotFromStringN_M(value, 4, 3) }
                onChange = { (value) => setLength(value) }
              />
            </Labeled>
          )}
          {['weightpipe'].includes(activeTab.id) && (
            <Labeled label = 'Количество труб в пачке, шт' variant = { lookAndFeel.labeledVariant }>
              <InputTextbox
                variant = { lookAndFeel.textboxVariant }
                value = { countPipes }
                valueTransformer = { (value: string) => Util.selectDigitsWithLimitFromString(value, 7) }
                onChange = { (value) => setCountPipes(value) }
              />
            </Labeled>
          )}
          {['weightpipe'].includes(activeTab.id) && (
            <Labeled label = 'Количество брака, шт' variant = { lookAndFeel.labeledVariant }>
              <InputTextbox
                variant = { lookAndFeel.textboxVariant }
                value = { countMarriage }
                valueTransformer = { (value: string) => Util.selectDigitsWithLimitFromString(value, 7) }
                onChange = { (value) => setCountMarriage(value) }
              />
            </Labeled>
          )}
          {['weight'].includes(activeTab.id) && (
            <Labeled label = 'Количество, шт' variant = { lookAndFeel.labeledVariant }>
              <InputTextbox
                variant = { lookAndFeel.textboxVariant }
                value = { count }
                valueTransformer = { (value: string) => Util.selectDigitsWithLimitFromString(value, 7) }
                onChange = { (value) => setCount(value) }
              />
            </Labeled>
          )}
          <ResultContainer lookAndFeel = { lookAndFeel }>
            <Result lookAndFeel = { lookAndFeel }>
              <ResultLabel lookAndFeel = { lookAndFeel }>
                { rlabels[activeTab.id] }
              </ResultLabel>
              {result > 0 && (
                <ResultValue lookAndFeel = { lookAndFeel }>
                  { `${activeTab.id === 'area' ? Math.round(result * 100) / 100 : Math.round(result)} ${runits[activeTab.id]}` }
                </ResultValue>
              )}
            </Result>
            <Button
              variant = { lookAndFeel.buttonVariant }
              label = 'Применить'
              isDisabled = { activeTab.id === 'length' || (activeTab.id === 'area' && !isAreaAcceptable) || result === 0 }
              onTap = { onTapAcceptHandler }
            />
          </ResultContainer>
        </React.Fragment>
      </Container>
    </Dialog>
  );
};
